// M2C2_BATTERY — Section 10.
//
// "Begin" no longer mounts an iframe. It parks the session in IndexedDB and
// navigates the whole page to the first assessment; the assessments chain to
// each other and the last one returns to the app, which resumes at
// SESSION_COMPLETE. See utils/m2c2Handoff.js for why — short version: an
// iframe shares the parent's main thread, and a GPU stall inside the game
// froze the entire app on Android, while the identical assessment run as its
// own page plays through fine on the same phone.
//
// Stop is only offered here on the intro screen; once the battery is running,
// m2c2kit's own in-task quit button is the only way out, and quitting mid-task
// KEEPS partial data (whatever the assessments already recorded is read back
// on return) rather than discarding it.

import { useState } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { beginBattery } from '../utils/m2c2Handoff';
import { logLine } from '../utils/m2c2Log';

export default function M2C2BatteryScreen({ onContinue }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const session = useSessionStore();
  const setM2C2Results = useSessionStore((s) => s.setM2C2Results);

  async function handleBegin() {
    setBusy(true);
    setError('');
    try {
      // This never returns normally — it navigates away. Anything after it
      // only runs if the handoff itself failed.
      await beginBattery(session.getSnapshot());
    } catch (err) {
      logLine(`ERROR: could not start battery — ${String(err)}`);
      setError('Could not start the activities. Your recordings are safe.');
      setBusy(false);
    }
  }

  function handleSkip() {
    // Stop pressed on the intro screen: audio was already recorded, so we
    // proceed to SESSION_COMPLETE with empty M2C2 data rather than discard
    // the session.
    setM2C2Results({ battery_complete: false, activities: {} });
    onContinue();
  }

  return (
    <div className="screen justify-center text-center">
      <h1 className="font-display text-2xl mb-3">A few quick activities</h1>
      <p className="text-dim mb-10">
        Next you'll do four short memory and attention games. Take your time — there's no rush.
      </p>

      {error && <p className="text-stop text-sm mb-4">{error}</p>}

      <button type="button" disabled={busy} onClick={handleBegin} className="btn-primary">
        {busy ? 'Starting…' : 'Begin'}
      </button>
      <button type="button" disabled={busy} onClick={handleSkip} className="btn-stop mt-3">
        Stop
      </button>
    </div>
  );
}
